import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  CheckCircleIcon,
  XCircleIcon,
  AlertCircleIcon,
  DiffIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LoaderIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import SmartPagination from "../shared/SmartPagination";
import { WorkflowTestRunItem } from "../../services/WorkflowService";

interface Props {
  items: WorkflowTestRunItem[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onCompareClick: (item: WorkflowTestRunItem) => void;
  onRowClick?: (item: WorkflowTestRunItem) => void;
  matchedFilter: boolean | null;
  onMatchedFilterChange: (val: boolean | null) => void;
}

function truncateJson(obj: unknown, maxLen: number = 120): string {
  if (obj === null || obj === undefined) return "—";
  const str = typeof obj === "string" ? obj : JSON.stringify(obj, null, 0);
  return str.length > maxLen ? str.slice(0, maxLen) + "…" : str;
}

const StatusIcon: React.FC<{ item: WorkflowTestRunItem }> = ({ item }) => {
  if (item.status === "ERROR") {
    return <AlertCircleIcon className="w-4 h-4 text-orange-500" />;
  }
  if (item.status === "CREATED" || item.status === "RUNNING") {
    return <LoaderIcon className="w-3.5 h-3.5 animate-spin text-muted-foreground" />;
  }
  if (item.matched === true) {
    return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
  }
  if (item.matched === false) {
    return <XCircleIcon className="w-4 h-4 text-red-500" />;
  }
  return <span className="text-muted-foreground">—</span>;
};

const FieldMatchRate: React.FC<{ item: WorkflowTestRunItem }> = ({ item }) => {
  const diff = item.diff_result;
  if (!diff || item.status !== "COMPLETED") {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  const rate = Math.round(diff.match_rate * 100);
  const color = rate === 100
    ? "text-green-600 dark:text-green-400"
    : rate >= 80
    ? "text-yellow-600 dark:text-yellow-400"
    : "text-red-600 dark:text-red-400";

  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className={cn("text-sm font-medium", color)}>{rate}%</span>
      <span className="text-xs text-muted-foreground">
        {diff.matched_count}/{diff.total_count}
      </span>
    </div>
  );
};

function formatOutputForTooltip(obj: unknown): string {
  if (obj === null || obj === undefined) return "—";
  return typeof obj === "string" ? obj : JSON.stringify(obj, null, 2);
}

interface OutputTooltipProps {
  children: React.ReactNode;
  item: WorkflowTestRunItem;
}

function formatFieldValue(val: unknown): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "string") return val;
  return JSON.stringify(val);
}

const statusColors: Record<string, string> = {
  match: "text-green-600 dark:text-green-400",
  mismatch: "text-red-600 dark:text-red-400",
  missing: "text-red-600 dark:text-red-400",
  extra: "text-yellow-600 dark:text-yellow-400",
};

const statusLabels: Record<string, string> = {
  match: "Match",
  mismatch: "Mismatch",
  missing: "Missing",
  extra: "Extra",
};

const OutputTooltip: React.FC<OutputTooltipProps> = ({ children, item }) => {
  const [isVisible, setIsVisible] = useState(false);
  const diff = item.diff_result;

  if (!diff || !diff.fields.length) {
    const expected = formatOutputForTooltip(item.expected_output);
    const actual = item.status === "ERROR"
      ? (item.error_message || "Error")
      : formatOutputForTooltip(item.actual_output);

    return (
      <div
        className="relative"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
        {isVisible && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-[9999] pointer-events-none">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 w-[500px] max-w-[90vw]">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                <DiffIcon className="w-4 h-4 text-maitai-green" />
                <span className="font-semibold text-foreground text-sm">Output Comparison</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2">Expected</div>
                  <div className="max-h-[200px] overflow-y-auto bg-gray-50 dark:bg-gray-900 rounded border p-2">
                    <pre className="whitespace-pre-wrap break-words text-xs text-foreground font-mono">
                      {expected}
                    </pre>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2">Actual</div>
                  <div className="max-h-[200px] overflow-y-auto bg-gray-50 dark:bg-gray-900 rounded border p-2">
                    <pre className="whitespace-pre-wrap break-words text-xs text-foreground font-mono">
                      {actual}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const rate = Math.round(diff.match_rate * 100);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-[9999] pointer-events-none">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 w-[600px] max-w-[90vw]">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <DiffIcon className="w-4 h-4 text-maitai-green" />
                <span className="font-semibold text-foreground text-sm">Field Diff</span>
              </div>
              <span className={cn(
                "text-sm font-medium",
                rate === 100 ? "text-green-600" : rate >= 80 ? "text-yellow-600" : "text-red-600"
              )}>
                {diff.matched_count}/{diff.total_count} fields ({rate}%)
              </span>
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Field</th>
                    <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Expected</th>
                    <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Actual</th>
                    <th className="text-center py-1.5 px-2 font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {diff.fields.map((field) => (
                    <tr
                      key={field.path}
                      className={cn(
                        "border-b border-gray-50 dark:border-gray-800",
                        field.status === "match" && "bg-green-50/50 dark:bg-green-900/10",
                        field.status === "mismatch" && "bg-red-50/50 dark:bg-red-900/10",
                        field.status === "missing" && "bg-red-50/50 dark:bg-red-900/10",
                        field.status === "extra" && "bg-yellow-50/50 dark:bg-yellow-900/10"
                      )}
                    >
                      <td className="py-1.5 px-2 font-mono text-foreground">{field.path}</td>
                      <td className="py-1.5 px-2 font-mono text-muted-foreground max-w-[160px] truncate" title={formatFieldValue(field.expected)}>
                        {formatFieldValue(field.expected)}
                      </td>
                      <td className="py-1.5 px-2 font-mono text-muted-foreground max-w-[160px] truncate" title={formatFieldValue(field.actual)}>
                        {formatFieldValue(field.actual)}
                      </td>
                      <td className={cn("py-1.5 px-2 text-center font-medium", statusColors[field.status])}>
                        {statusLabels[field.status]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ExpandableCell: React.FC<{ content: unknown }> = ({ content }) => {
  const [expanded, setExpanded] = useState(false);

  if (content === null || content === undefined) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  const full = typeof content === "string" ? content : JSON.stringify(content, null, 2);
  const short = truncateJson(content);
  const needsExpand = full.length > 120;

  return (
    <div className="space-y-1">
      <pre className="text-xs font-mono whitespace-pre-wrap break-all leading-relaxed max-w-[240px]">
        {expanded ? full : short}
      </pre>
      {needsExpand && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="inline-flex items-center gap-0.5 text-xs text-maitai-green hover:underline"
        >
          {expanded ? (
            <>
              <ChevronUpIcon className="w-3 h-3" /> Less
            </>
          ) : (
            <>
              <ChevronDownIcon className="w-3 h-3" /> More
            </>
          )}
        </button>
      )}
    </div>
  );
};

const WorkflowTestRunItemsTable: React.FC<Props> = ({
  items,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onCompareClick,
  onRowClick,
  matchedFilter,
  onMatchedFilterChange,
}) => {
  const filterOptions: { label: string; value: boolean | null }[] = [
    { label: "All", value: null },
    { label: "Passed", value: true },
    { label: "Failed", value: false },
  ];

  if (loading && items.length === 0) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-3">
            <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
            <div className="h-4 bg-muted rounded w-20 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter row */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Filter:</span>
        {filterOptions.map((opt) => (
          <Badge
            key={opt.label}
            variant={matchedFilter === opt.value ? "default" : "outline"}
            className={cn(
              "cursor-pointer transition-colors",
              matchedFilter === opt.value
                ? "bg-maitai-green text-white hover:bg-maitai-green/90"
                : "hover:bg-muted"
            )}
            onClick={() => onMatchedFilterChange(opt.value)}
          >
            {opt.label}
          </Badge>
        ))}
        {matchedFilter !== null && (
          <button
            className="text-xs text-muted-foreground hover:text-foreground underline ml-1"
            onClick={() => onMatchedFilterChange(null)}
          >
            Clear
          </button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow className="border-b border-border hover:bg-transparent">
            <TableHead className="w-[5%] text-center">Result</TableHead>
            <TableHead className="w-[10%] text-center">Match</TableHead>
            <TableHead className="w-[25%] text-left">Input</TableHead>
            <TableHead className="w-[20%] text-left">Expected Output</TableHead>
            <TableHead className="w-[20%] text-left">Actual Output</TableHead>
            <TableHead className="w-[8%] text-center">Duration</TableHead>
            <TableHead className="w-[7%] text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                {matchedFilter !== null ? "No items match this filter." : "No items yet."}
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow
                key={item.id}
                className={cn(
                  "border-b border-border/50 align-top",
                  onRowClick && "cursor-pointer hover:bg-accent/20 transition-colors"
                )}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
              >
                <TableCell className="py-4 text-center">
                  <div className="flex items-center justify-center">
                    <StatusIcon item={item} />
                  </div>
                </TableCell>
                <TableCell className="py-4 text-center">
                  <FieldMatchRate item={item} />
                </TableCell>
                <TableCell className="py-4 text-left">
                  <ExpandableCell content={item.input} />
                </TableCell>
                <TableCell className="py-4 text-left">
                  <OutputTooltip item={item}>
                    <ExpandableCell content={item.expected_output} />
                  </OutputTooltip>
                </TableCell>
                <TableCell className="py-4 text-left">
                  <OutputTooltip item={item}>
                    {item.status === "ERROR" ? (
                      <span className="text-xs text-red-500 font-mono">{item.error_message || "Error"}</span>
                    ) : (
                      <ExpandableCell content={item.actual_output} />
                    )}
                  </OutputTooltip>
                </TableCell>
                <TableCell className="py-4 text-center">
                  <span className="text-sm text-muted-foreground">
                    {item.duration_ms != null ? `${item.duration_ms}ms` : "—"}
                  </span>
                </TableCell>
                <TableCell className="py-4 text-center">
                  {item.status === "COMPLETED" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCompareClick(item);
                      }}
                      title="Compare expected vs actual"
                    >
                      <DiffIcon className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <SmartPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          itemName="items"
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
        />
      )}
    </div>
  );
};

export default WorkflowTestRunItemsTable;
